import type {
  HeaderLabel,
  HoursClaFlag,
  HoursFlagGate
} from '../../columns/columnSpec.types';

/**
 * The Dojo form was a FormBuilder `fields` array of separators, full-width text labels and
 * two-column "compound" rows (`EmployeeHoursCorrectionForm.js`, 1060 lines). Every row is one of
 * five shapes, so the form becomes data here too.
 */
export type CorrectionRowSpec =
  | {
      kind: 'section';
      label: HeaderLabel;
      requiresFlag?: HoursClaFlag;
      requiresBalanceBudget?: true;
    }
  | { kind: 'caption'; label: HeaderLabel; requiresFlag?: HoursClaFlag }
  | {
      kind: 'pair';
      label: HeaderLabel;
      /** left column: the current value, read-only */
      currentKey?: CorrectionReadOnlyKey;
      /** right column: the editable correction */
      editKey?: CorrectionEditableKey;
      /** display sign follows the plus/minus org preference */
      signFlipWhenPlusMinus?: true;
      requiresFlag?: HoursFlagGate;
      excludedByFlag?: HoursClaFlag;
      requiresBalanceBudget?: true;
    }
  | { kind: 'flag'; label: HeaderLabel; flagName: 'FORCE_SALARY_EXPORT' }
  | { kind: 'memo'; label: HeaderLabel; editKey: 'inputWorkingMemo' }
  | { kind: 'note'; label: HeaderLabel; requiresBalanceBudget?: true };

/**
 * Editable keys. This set must stay identical to `MNG_CORRECTION_FIELDS` in
 * `EmployeeDelegate.groovy:90-125` — the backend does `keySet().retainAll(MNG_CORRECTION_FIELDS)`, so
 * anything outside it is silently discarded on save.
 */
export type CorrectionEditableKey =
  | 'inputJus'
  | 'inputWorkingHoursCor'
  | 'inputHoursLeaveMin'
  | 'inputHoursLeaveMinPrev'
  | 'inputHoursLeaveOver'
  | 'leaveOneTimeDeposit'
  | 'inputHoursLeaveOverPrev'
  | 'inputHoursLeave1Cor'
  | 'inputHoursLeave2Cor'
  | 'inputHoursLeave3Cor'
  | 'inputHoursLeave4Cor'
  | 'inputHoursLeave5Cor'
  | 'inputHoursAgeCor'
  | 'inputHoursPlbCor'
  | 'plbOneTimeDeposit'
  | 'inputHoursPlbPrevCor'
  | 'inputHoursPlb1Cor'
  | 'inputHoursPlb2Cor'
  | 'inputHoursPlb3Cor'
  | 'inputHoursPlb4Cor'
  | 'inputHoursPlb5Cor'
  | 'inputHoursPlb6Cor'
  | 'inputHoursPlb7Cor'
  | 'inputHoursPlb8Cor'
  | 'inputHoursPlb9Cor'
  | 'inputHoursPlb10Cor'
  | 'hoursPlbUsedWork'
  | 'hoursPlbUsedLv'
  | 'hoursPlbUsedLcs'
  | 'inputHoursLcs'
  | 'hoursLcsUsedFin'
  | 'hoursLcsUsedLife'
  | 'inputWorkingMemo';

/** Read-only values shown beside a correction input. Never sent back. */
export type CorrectionReadOnlyKey =
  | 'hoursBalanceBase'
  | 'allHoursWorkTotal'
  | 'hoursLeaveMin'
  | 'leaveMinPrev'
  | 'leaveOver'
  | 'leaveOneTimeDepositRato'
  | 'leaveOverPrev'
  | 'overLeaveAge1'
  | 'overLeaveAge2'
  | 'overLeaveAge3'
  | 'overLeaveAge4'
  | 'overLeaveAge5'
  | 'hoursCompLeaveAll'
  | 'hoursHolidayBalanceCum'
  | 'plbCurr'
  | 'plbOneTimeDepositRato'
  | 'plbAgePrev'
  | 'plbAge1'
  | 'plbAge2'
  | 'plbAge3'
  | 'plbAge4'
  | 'plbAge5'
  | 'plbAge6'
  | 'plbAge7'
  | 'plbAge8'
  | 'plbAge9'
  | 'plbAge10'
  | 'hoursPlbBalanceCum'
  | 'hoursLcsBalanceCum'
  | 'savedPlusHours'
  | 'savedExtraStatutoryLeaveHours'
  | 'savedPLBHours';

/** The FORCE_SALARY_EXPORT flag lives inside the `flags` array, not as its own field. */
export const FORCE_SALARY_EXPORT = 'FORCE_SALARY_EXPORT';
